import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Rating,
  Button,
  Select,
  MenuItem,
  Tooltip,
  LinearProgress,
  useMediaQuery,
  Pagination,
  Paper,
  Stack,
  Divider,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined";
import RateReviewRoundedIcon from "@mui/icons-material/RateReviewRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SortRoundedIcon from "@mui/icons-material/SortRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import EditNoteRoundedIcon from "@mui/icons-material/EditNoteRounded";
import ReviewsRoundedIcon from "@mui/icons-material/ReviewsRounded";
import { UserContext } from "../../context/userContext/userContext";
import { useStyle } from "../../context/styleContext/styleContext";
import LoginRegisterModal from "./LoginRegisterModal";
import LeaveReviewModal from "./leaveReviewModal";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import Seo from "../../components/Seo";
import { Link, useParams } from "react-router-dom";
import ProductReviewSchemaMarkup from "./ProductReviewSchemaMarkup";

const REVIEWS_PER_PAGE = 6;

export default function ProductReviewPage() {
  const { user } = useContext(UserContext);
  const { style } = useStyle();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { itemName } = useParams();

  const [reviews, setReviews] = useState([]);
  const [productSKU, setProductSKU] = useState(null);
  const [productData, setProductData] = useState(null);

  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("recent");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [ratingsBreakdown, setRatingsBreakdown] = useState([0, 0, 0, 0, 0]);
  const [averageRating, setAverageRating] = useState(0);
  const [height, setHeight] = useState(window.innerHeight);

  const [isLoginRegisterModalOpen, setLoginRegisterModalOpen] = useState(false);
  const [isLeaveReviewModalOpen, setLeaveReviewModalOpen] = useState(false);

  const [storedReviewId, setStoredReviewId] = useState(null);
  const [storedVote, setStoredVote] = useState(null);
  const [pendingOpenReviewModalAfterLogin, setPendingOpenReviewModalAfterLogin] = useState(false);

  const [myReview, setMyReview] = useState(null);

  const closeModal = (setModalFn) => () => setModalFn(false);

  useEffect(() => {
    loadPageData();
  }, [itemName]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter, sort, search]);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const loadPageData = async () => {
    try {
      const { data: productInfo } = await axios.post("/get_product_by_uri", {
        productURI: itemName,
      });

      if (!productInfo) return;

      setProductSKU(productInfo.SKU);
      setProductData(productInfo);

      await Promise.all([
        getReviews(productInfo.SKU),
        getMyReview(productInfo.SKU),
      ]);
    } catch (error) {
      console.log("Error loading review page:", error);
    }
  };

  const getReviews = async (skuOverride = productSKU) => {
    try {
      if (!skuOverride) return;

      const token = localStorage.getItem("token");

      const { data } = await axios.post("/get_all_product_reviews", {
        productSKU: skuOverride,
        token,
      });

      const loadedReviews = data || [];
      setReviews(loadedReviews);

      const starCounts = [0, 0, 0, 0, 0];
      let total = 0;
      let sum = 0;

      loadedReviews.forEach((review) => {
        const starValue = Number(review.starRating || 0);
        const bucket = Math.max(1, Math.min(5, Math.floor(starValue)));
        starCounts[5 - bucket] += 1;
        total += 1;
        sum += starValue;
      });

      setAverageRating(total ? Number((sum / total).toFixed(1)) : 0);
      setRatingsBreakdown(starCounts);
    } catch (error) {
      console.log("Error loading reviews:", error);
    }
  };

  const getMyReview = async (skuOverride = productSKU) => {
    try {
      const token = localStorage.getItem("token");

      if (!token || !skuOverride || !user?.id) {
        setMyReview(null);
        return;
      }

      const { data } = await axios.post("/api/get_my_product_review", {
        token,
        productSKU: skuOverride,
        ownerID: user.id,
      });

      setMyReview(data?.review || null);
    } catch (error) {
      console.log("Error loading my review:", error);
      setMyReview(null);
    }
  };

  useEffect(() => {
    if (user?.id && productSKU) {
      getMyReview(productSKU);
    } else if (!user?.id) {
      setMyReview(null);
    }
  }, [user?.id, productSKU]);

  const loginCallback = async () => {
    const token = localStorage.getItem("token");

    if (token && productSKU) {
      await getMyReview(productSKU);
    }

    if (token && storedReviewId && storedVote) {
      try {
        await axios.post("/api/mark_review", {
          token,
          reviewId: storedReviewId,
          type: storedVote,
        });

        setStoredReviewId(null);
        setStoredVote(null);
        setLoginRegisterModalOpen(false);
        await getReviews(productSKU);
      } catch (error) {
        console.log("Error marking review vote after login:", error);
      }
    }

    if (pendingOpenReviewModalAfterLogin) {
      setPendingOpenReviewModalAfterLogin(false);
      setLoginRegisterModalOpen(false);
      await getMyReview(productSKU);
      setLeaveReviewModalOpen(true);
    }
  };

  const handleVote = async (reviewId, type) => {
    const token = localStorage.getItem("token");

    if (!token || !user) {
      setStoredVote(type);
      setStoredReviewId(reviewId);
      setLoginRegisterModalOpen(true);
      return;
    }

    try {
      await axios.post("/api/mark_review", {
        token,
        reviewId,
        type,
      });

      await getReviews();
      await getMyReview();
    } catch (error) {
      console.log("Error marking review vote:", error);
    }
  };

  const openReviewModal = async () => {
    const token = localStorage.getItem("token");

    if (!token || !user) {
      setPendingOpenReviewModalAfterLogin(true);
      setLoginRegisterModalOpen(true);
      return;
    }

    await getMyReview(productSKU);
    setLeaveReviewModalOpen(true);
  };

  const formatDate = (dateStr) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateStr).toLocaleDateString(undefined, options);
  };

  const reviewSearchFiltered = useMemo(() => {
    let result = [...reviews];

    if (filter !== "all") {
      const numericFilter = Number(filter);
      result = result.filter(
        (review) =>
          Math.max(1, Math.min(5, Math.floor(Number(review.starRating || 0)))) ===
          numericFilter
      );
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((review) => {
        const userName = (review.userName || "").toLowerCase();
        const title = (review.title || "").toLowerCase();
        const comment = (review.comment || "").toLowerCase();
        return (
          userName.includes(q) ||
          title.includes(q) ||
          comment.includes(q)
        );
      });
    }

    if (sort === "recent") {
      result.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    } else if (sort === "helpful") {
      result.sort((a, b) => {
        const helpfulA = Number(a.helpfulCount || 0) - Number(a.notHelpfulCount || 0);
        const helpfulB = Number(b.helpfulCount || 0) - Number(b.notHelpfulCount || 0);
        if (helpfulB !== helpfulA) return helpfulB - helpfulA;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
    } else if (sort === "highest") {
      result.sort((a, b) => Number(b.starRating) - Number(a.starRating));
    } else if (sort === "lowest") {
      result.sort((a, b) => Number(a.starRating) - Number(b.starRating));
    }

    return result;
  }, [reviews, filter, sort, search]);

  const totalPages = Math.max(1, Math.ceil(reviewSearchFiltered.length / REVIEWS_PER_PAGE));

  const paginatedReviews = useMemo(() => {
    return reviewSearchFiltered.slice(
      (currentPage - 1) * REVIEWS_PER_PAGE,
      currentPage * REVIEWS_PER_PAGE
    );
  }, [reviewSearchFiltered, currentPage]);

  const reviewButtonLabel = myReview ? "Edit Your Review" : "Write a Review";

  const sharedSelectSx = {
    minWidth: { xs: "100%", sm: 180 },
    color: style.color4,
    backgroundColor: style.darkPanel,
    borderRadius: "8px",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: style.color2,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: style.color3,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: style.accentGreen,
    },
    "& .MuiSelect-icon": {
      color: style.color4,
    },
  };

  return (
    <>
      <Seo
        title={
          productData
            ? `${productData.name} Reviews | Parish Audio`
            : "Plugin Reviews | Parish Audio"
        }
        description={
          productData
            ? `Read customer reviews for ${productData.name} by Parish Audio. See ratings, feedback, and user impressions for this VST3 plugin.`
            : "Read customer reviews for Parish Audio plugins."
        }
        canonical={
          productData
            ? `https://parishaudio.com/reviews/${productData.productURI}`
            : "https://parishaudio.com/reviews"
        }
        image={
          productData?.images?.[0]?.URI
            ? `https://parishaudio.com${productData.images[0].URI}`
            : "https://parishaudio.com/assets/og-default.jpg"
        }
        imageAlt={
          productData?.images?.[0]?.altText || productData?.name || "Parish Audio reviews"
        }
        ogType="website"
      />

      {productData && (
        <ProductReviewSchemaMarkup
          productData={productData}
          reviews={reviews}
          averageRating={averageRating}
        />
      )}

      {!productSKU ? (
        <Box
          sx={{
            minHeight: height - 170,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <LoadingSpinner />
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            maxWidth: "100vw",
            overflowX: "hidden",
            px: { xs: 1.25, sm: 1.5, md: 2.5 },
            py: { xs: 2, md: 3 },
            display: "flex",
            justifyContent: "center",
            boxSizing: "border-box",
            minHeight: height - 170,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: 1120,
              minWidth: 0,
              boxSizing: "border-box",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                minWidth: 0,
                borderRadius: "20px",
                overflow: "hidden",
                backgroundColor: style.color1,
                border: `1px solid ${style.color2}`,
                boxSizing: "border-box",
              }}
            >
              <Box
                sx={{
                  px: { xs: 1.5, sm: 2, md: 3 },
                  py: { xs: 2.25, md: 3 },
                  borderBottom: `1px solid ${style.color2}`,
                  backgroundColor: style.darkPanel,
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  alignItems={{ xs: "flex-start", md: "center" }}
                  sx={{ minWidth: 0 }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      width: { xs: "100%", md: 250 },
                      minWidth: 0,
                      p: 1.5,
                      borderRadius: "14px",
                      backgroundColor: style.darkerPanel,
                      border: "1px solid rgba(255,255,255,0.08)",
                      boxSizing: "border-box",
                      textAlign: "center",
                    }}
                  >
                    <Link
                      to={`/products/${productData.productURI}`}
                      style={{ textDecoration: "none", color: "inherit" }}
                    >
                      <Box
                        component="img"
                        src={productData.images?.[0]?.URI}
                        alt={productData.images?.[0]?.altText}
                        sx={{
                          width: "100%",
                          maxHeight: 220,
                          objectFit: "contain",
                          display: "block",
                          mx: "auto",
                        }}
                      />
                      <Typography
                        sx={{
                          mt: 1.25,
                          fontSize: "1.05rem",
                          color: style.color4,
                          fontWeight: 800,
                          lineHeight: 1.25,
                        }}
                      >
                        {productData.name}
                      </Typography>
                    </Link>

                    <Button
                      component={Link}
                      to={`/products/${productData.productURI}`}
                      sx={{
                        mt: 1.5,
                        borderRadius: "5px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.05)",
                        border: "1px solid rgba(255,255,255,0.08)",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.10)",
                        },
                      }}
                    >
                      View Product
                    </Button>
                  </Paper>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack spacing={1.25}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                      >
                        <ReviewsRoundedIcon sx={{ color: style.color3 }} />
                        <Typography
                          sx={{
                            color: style.color4,
                            fontSize: { xs: "1.7rem", md: "2.3rem" },
                            fontWeight: 800,
                            lineHeight: 1.05,
                            letterSpacing: "-0.03em",
                          }}
                        >
                          {productData.name} Reviews
                        </Typography>
                      </Stack>

                      <Stack
                        direction={{ xs: "column", lg: "row" }}
                        spacing={2}
                        sx={{ pt: 0.5 }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.5,
                            borderRadius: "14px",
                            backgroundColor: style.darkerPanel,
                            border: "1px solid rgba(255,255,255,0.08)",
                            minWidth: { lg: 260 },
                          }}
                        >
                          <Stack spacing={0.9}>
                            <Stack direction="row" spacing={1.25} alignItems="center">
                              <Rating value={parseFloat(averageRating)} precision={0.1} readOnly />
                              <Typography
                                sx={{ color: style.color4, fontWeight: 800, fontSize: "1.05rem" }}
                              >
                                {averageRating} / 5
                              </Typography>
                            </Stack>

                            <Typography sx={{ color: style.color3, fontSize: "0.92rem" }}>
                              Based on {reviews.length} review{reviews.length === 1 ? "" : "s"}
                            </Typography>

                            {myReview && (
                              <Chip
                                size="small"
                                icon={<EditRoundedIcon />}
                                label="You reviewed this product"
                                sx={{
                                  width: "fit-content",
                                  borderRadius: "5px",
                                  color: style.color4,
                                  backgroundColor: "rgba(88,166,124,0.16)",
                                  border: "1px solid rgba(88,166,124,0.30)",
                                }}
                              />
                            )}
                          </Stack>
                        </Paper>

                        <Paper
                          elevation={0}
                          sx={{
                            flex: 1,
                            p: 1.5,
                            borderRadius: "14px",
                            backgroundColor: style.darkerPanel,
                            border: "1px solid rgba(255,255,255,0.08)",
                          }}
                        >
                          <Stack spacing={0.95}>
                            {[5, 4, 3, 2, 1].map((star) => {
                              const count = ratingsBreakdown[5 - star] || 0;
                              const percent = reviews.length ? (count / reviews.length) * 100 : 0;

                              return (
                                <Box
                                  key={star}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.25,
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    spacing={0.4}
                                    alignItems="center"
                                    sx={{ width: 54 }}
                                  >
                                    <Typography sx={{ color: style.color4, fontSize: "0.95rem" }}>
                                      {star}
                                    </Typography>
                                    <StarRoundedIcon sx={{ color: style.color3, fontSize: 18 }} />
                                  </Stack>

                                  <LinearProgress
                                    variant="determinate"
                                    value={percent}
                                    sx={{
                                      flexGrow: 1,
                                      height: 10,
                                      borderRadius: 999,
                                      backgroundColor: "rgba(255,255,255,0.14)",
                                      "& .MuiLinearProgress-bar": {
                                        backgroundColor: style.accentGreen,
                                      },
                                    }}
                                  />

                                  <Typography
                                    sx={{
                                      width: 28,
                                      textAlign: "right",
                                      color: style.color4,
                                      fontSize: "0.92rem",
                                      fontWeight: 700,
                                    }}
                                  >
                                    {count}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Stack>
                        </Paper>
                      </Stack>
                    </Stack>
                  </Box>
                </Stack>
              </Box>

              <Box
                sx={{
                  px: { xs: 1.5, sm: 2, md: 3 },
                  py: { xs: 1.5, md: 2.5 },
                }}
              >
                <Stack
                  direction={{ xs: "column", lg: "row" }}
                  spacing={1.25}
                  alignItems={{ xs: "stretch", lg: "center" }}
                  justifyContent="space-between"
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.25}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    sx={{ flex: 1 }}
                  >
                    <TextField
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search reviews"
                      size="small"
                      sx={{
                        flex: 1,
                        minWidth: { sm: 240 },
                        "& .MuiInputBase-root": {
                          color: style.color4,
                          backgroundColor: style.darkPanel,
                          borderRadius: "8px",
                        },
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: style.color2,
                          },
                          "&:hover fieldset": {
                            borderColor: style.color3,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: style.accentGreen,
                          },
                        },
                        "& .MuiInputBase-input::placeholder": {
                          color: style.color3,
                          opacity: 1,
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchRoundedIcon sx={{ color: style.color3 }} />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Select
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      size="small"
                      sx={sharedSelectSx}
                    >
                      <MenuItem value="all">All Ratings</MenuItem>
                      {[5, 4, 3, 2, 1].map((num) => (
                        <MenuItem key={num} value={num}>
                          {num} Stars
                        </MenuItem>
                      ))}
                    </Select>

                    <Select
                      value={sort}
                      onChange={(e) => setSort(e.target.value)}
                      size="small"
                      sx={sharedSelectSx}
                      startAdornment={
                        !isMobile ? (
                          <InputAdornment position="start">
                            <SortRoundedIcon sx={{ color: style.color3, ml: 1 }} />
                          </InputAdornment>
                        ) : undefined
                      }
                    >
                      <MenuItem value="recent">Most Recent</MenuItem>
                      <MenuItem value="helpful">Most Helpful</MenuItem>
                      <MenuItem value="highest">Highest Rated</MenuItem>
                      <MenuItem value="lowest">Lowest Rated</MenuItem>
                    </Select>
                  </Stack>

                  <Button
                    onClick={openReviewModal}
                    startIcon={myReview ? <EditRoundedIcon /> : <RateReviewRoundedIcon />}
                    sx={{
                      borderRadius: "5px",
                      textTransform: "none",
                      fontWeight: 700,
                      color: style.color4,
                      backgroundColor: style.accentGreen,
                      minWidth: { xs: "100%", lg: 170 },
                      "&:hover": {
                        backgroundColor: style.accentGreen,
                        filter: "brightness(1.05)",
                      },
                    }}
                  >
                    {reviewButtonLabel}
                  </Button>
                </Stack>

                <Divider sx={{ backgroundColor: style.color2, my: 2 }} />

                {paginatedReviews.length === 0 ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "14px",
                      backgroundColor: style.darkPanel,
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.5 }}>
                      No matching reviews
                    </Typography>
                    <Typography sx={{ color: style.color3 }}>
                      Try changing your filters or be the first to leave a review.
                    </Typography>
                  </Paper>
                ) : (
                  <Stack spacing={1.5}>
                    {paginatedReviews.map((review) => {
                      const helpfulSelected =
                        user && review.foundHelpful?.includes(user.id);
                      const notHelpfulSelected =
                        user && review.foundNotHelpful?.includes(user.id);
                      const isOwnReview = !!(user && review.ownerID && review.ownerID === user.id);

                      return (
                        <Card
                          key={review._id}
                          sx={{
                            backgroundColor: style.darkPanel,
                            color: style.color4,
                            borderRadius: "14px",
                            border: "1px solid rgba(255,255,255,0.08)",
                            boxShadow: "none",
                            overflow: "hidden",
                          }}
                        >
                          <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
                            <Stack spacing={1.25}>
                              <Stack
                                direction={{ xs: "column", sm: "row" }}
                                justifyContent="space-between"
                                alignItems={{ xs: "flex-start", sm: "center" }}
                                spacing={1}
                              >
                                <Stack direction="row" spacing={1.2} alignItems="center">
                                  <Avatar
                                    sx={{
                                      width: 38,
                                      height: 38,
                                      backgroundColor: style.color2,
                                      color: style.color4,
                                    }}
                                  >
                                    <PersonRoundedIcon fontSize="small" />
                                  </Avatar>

                                  <Box>
                                    <Stack
                                      direction="row"
                                      spacing={0.75}
                                      alignItems="center"
                                      flexWrap="wrap"
                                      useFlexGap
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: "1rem",
                                          fontWeight: 800,
                                          color: style.color4,
                                        }}
                                      >
                                        {review.userName || "Anonymous"}
                                      </Typography>

                                      {isOwnReview && (
                                        <Chip
                                          size="small"
                                          label="Your review"
                                          sx={{
                                            borderRadius: "5px",
                                            color: style.color4,
                                            backgroundColor: "rgba(88,166,124,0.16)",
                                            border: "1px solid rgba(88,166,124,0.30)",
                                          }}
                                        />
                                      )}

                                      {review.edited && (
                                        <Chip
                                          size="small"
                                          icon={<EditNoteRoundedIcon />}
                                          label="Edited"
                                          sx={{
                                            borderRadius: "5px",
                                            color: style.color4,
                                            backgroundColor: "rgba(255,255,255,0.08)",
                                          }}
                                        />
                                      )}
                                    </Stack>

                                    <Stack
                                      direction="row"
                                      spacing={1}
                                      alignItems="center"
                                      flexWrap="wrap"
                                      useFlexGap
                                      sx={{ mt: 0.35 }}
                                    >
                                      <Rating value={Number(review.starRating)} readOnly precision={0.5} size="small" />
                                      <Stack direction="row" spacing={0.5} alignItems="center">
                                        <CalendarTodayRoundedIcon sx={{ color: style.color3, fontSize: 14 }} />
                                        <Typography
                                          sx={{
                                            color: style.color3,
                                            fontSize: "0.82rem",
                                          }}
                                        >
                                          {formatDate(review.createdAt)}
                                        </Typography>
                                      </Stack>
                                    </Stack>
                                  </Box>
                                </Stack>
                              </Stack>

                              {!!review.title && (
                                <Typography
                                  sx={{
                                    color: style.color4,
                                    fontWeight: 800,
                                    fontSize: "1.02rem",
                                  }}
                                >
                                  {review.title}
                                </Typography>
                              )}

                              <Typography
                                sx={{
                                  color: style.color4,
                                  lineHeight: 1.7,
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                {review.comment}
                              </Typography>

                              {review.edited && (
                                <Typography
                                  sx={{
                                    color: style.color3,
                                    fontSize: "0.82rem",
                                    fontStyle: "italic",
                                  }}
                                >
                                  This review was edited after it was originally posted.
                                </Typography>
                              )}

                              <Stack
                                direction="row"
                                spacing={0.75}
                                alignItems="center"
                                flexWrap="wrap"
                                useFlexGap
                                sx={{ pt: 0.25 }}
                              >
                                <Tooltip title="Helpful">
                                  <Button
                                    onClick={() => handleVote(review._id, "helpful")}
                                    startIcon={
                                      <ThumbUpAltOutlinedIcon
                                        sx={{
                                          color: helpfulSelected ? style.accentGreen : style.color3,
                                        }}
                                      />
                                    }
                                    sx={{
                                      borderRadius: "5px",
                                      textTransform: "none",
                                      fontWeight: 700,
                                      color: style.color4,
                                      backgroundColor: helpfulSelected
                                        ? "rgba(88,166,124,0.14)"
                                        : "rgba(255,255,255,0.05)",
                                      border: "1px solid rgba(255,255,255,0.08)",
                                      "&:hover": {
                                        backgroundColor: "rgba(255,255,255,0.10)",
                                      },
                                    }}
                                  >
                                    {review.helpfulCount || 0}
                                  </Button>
                                </Tooltip>

                                <Tooltip title="Not Helpful">
                                  <Button
                                    onClick={() => handleVote(review._id, "not_helpful")}
                                    startIcon={
                                      <ThumbDownAltOutlinedIcon
                                        sx={{
                                          color: notHelpfulSelected ? "#ef5350" : style.color3,
                                        }}
                                      />
                                    }
                                    sx={{
                                      borderRadius: "5px",
                                      textTransform: "none",
                                      fontWeight: 700,
                                      color: style.color4,
                                      backgroundColor: notHelpfulSelected
                                        ? "rgba(239,83,80,0.12)"
                                        : "rgba(255,255,255,0.05)",
                                      border: "1px solid rgba(255,255,255,0.08)",
                                      "&:hover": {
                                        backgroundColor: "rgba(255,255,255,0.10)",
                                      },
                                    }}
                                  >
                                    {review.notHelpfulCount || 0}
                                  </Button>
                                </Tooltip>
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                )}

                {totalPages > 1 && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={(e, value) => setCurrentPage(value)}
                      color="primary"
                      sx={{
                        "& .MuiPaginationItem-root": {
                          color: style.color4,
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Paper>

            <LoginRegisterModal
              isOpen={isLoginRegisterModalOpen}
              close={closeModal(setLoginRegisterModalOpen)}
              loginComplete={loginCallback}
            />

            <LeaveReviewModal
              sku={productSKU}
              existingReview={myReview}
              productName={productData?.name}
              isOpen={isLeaveReviewModalOpen}
              close={closeModal(setLeaveReviewModalOpen)}
              onSaved={async () => {
                await getReviews();
                await getMyReview();
              }}
              onDeleted={async () => {
                await getReviews();
                await getMyReview();
              }}
            />
          </Box>
        </Box>
      )}
    </>
  );
}

// import React, { useState, useContext, useEffect, useMemo } from "react";
// import axios from "axios";
// import {
//   Box,
//   Card,
//   CardContent,
//   Typography,
//   Rating,
//   Button,
//   Select,
//   MenuItem,
//   IconButton,
//   Tooltip,
//   LinearProgress,
//   useMediaQuery,
//   Pagination,
//   Paper,
//   Stack,
//   Divider,
// } from "@mui/material";
// import { useTheme } from "@mui/material/styles";
// import ThumbUpIcon from "@mui/icons-material/ThumbUp";
// import ThumbDownIcon from "@mui/icons-material/ThumbDown";
// import { UserContext } from "../../context/userContext/userContext";
// import { useStyle } from "../../context/styleContext/styleContext";
// import SubscribeRedirectModal from "../../components/SubscribeRedirectModal/SubscribeRedirectModal";
// import LoginRegisterModal from "./LoginRegisterModal";
// import LeaveReviewModal from "./leaveReviewModal";
// import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
// import Seo from "../../components/Seo";
// import { Link, useParams } from "react-router-dom";
// import ProductReviewSchemaMarkup from "./ProductReviewSchemaMarkup";

// const REVIEWS_PER_PAGE = 3;

// export default function ProductReviewPage() {
//   const { user } = useContext(UserContext);
//   const { style } = useStyle();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
//   const { itemName } = useParams();

//   const [reviews, setReviews] = useState([]);
//   const [productSKU, setProductSKU] = useState(null);
//   const [productData, setProductData] = useState(null);
//   const [filter, setFilter] = useState("all");
//   const [sort, setSort] = useState("recent");
//   const [currentPage, setCurrentPage] = useState(1);
//   const [ratingsBreakdown, setRatingsBreakdown] = useState([0, 0, 0, 0, 0]);
//   const [averageRating, setAverageRating] = useState(0);
//   const [height, setHeight] = useState(window.innerHeight);

//   const [isSubscribeRedirectModalOpen, setSubscribeRedirectModalOpen] = useState(false);
//   const [isLoginRegisterModalOpen, setLoginRegisterModalOpen] = useState(false);
//   const [isLeaveReviewModalOpen, setLeaveReviewModalOpen] = useState(false);

//   const [storedReviewId, setStoredReviewId] = useState(null);
//   const [storedVote, setStoredVote] = useState(null);

//   const closeModal = (setModalFn) => () => setModalFn(false);

//   useEffect(() => {
//     getReviews();
//   }, [itemName, filter, sort]);

//   useEffect(() => {
//     setCurrentPage(1);
//   }, [filter, sort]);

//   useEffect(() => {
//     function handleResize() {
//       setHeight(window.innerHeight);
//     }

//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   const loginCallback = async () => {
//     const token = localStorage.getItem("token");
//     if (!token || !storedReviewId || !storedVote) {
//       return;
//     }

//     try {
//       await axios.post("/api/mark_review", {
//         token,
//         reviewId: storedReviewId,
//         type: storedVote,
//       });

//       setLoginRegisterModalOpen(false);
//       setStoredReviewId(null);
//       setStoredVote(null);
//       getReviews();
//     } catch (error) {
//       console.log("Error marking review vote after login:", error);
//     }
//   };

//   const getReviews = async () => {
//     try {
//       const { data: productInfo } = await axios.post("/get_product_by_uri", {
//         productURI: itemName,
//       });

//       if (!productInfo) return;

//       setProductSKU(productInfo.SKU);
//       setProductData(productInfo);

//       const { data } = await axios.post("/get_all_product_reviews", {
//         productSKU: productInfo.SKU,
//         filter,
//         sort,
//       });

//       const loadedReviews = data || [];
//       setReviews(loadedReviews);

//       const starCounts = [0, 0, 0, 0, 0];
//       let total = 0;
//       let sum = 0;

//       loadedReviews.forEach((review) => {
//         const index = 5 - Number(review.starRating);
//         if (index >= 0 && index < 5) {
//           starCounts[index] += 1;
//         }
//         total += 1;
//         sum += Number(review.starRating || 0);
//       });

//       setAverageRating(total ? Number((sum / total).toFixed(1)) : 0);
//       setRatingsBreakdown(starCounts);
//     } catch (error) {
//       console.log("Error loading reviews:", error);
//     }
//   };

//   const handleVote = async (reviewId, type) => {
//     const token = localStorage.getItem("token");

//     if (!token || !user) {
//       setStoredVote(type);
//       setStoredReviewId(reviewId);
//       setLoginRegisterModalOpen(true);
//       return;
//     }

//     try {
//       await axios.post("/api/mark_review", {
//         token,
//         reviewId,
//         type,
//       });

//       getReviews();
//     } catch (error) {
//       console.log("Error marking review vote:", error);
//     }
//   };

//   const formatDate = (dateStr) => {
//     const options = { year: "numeric", month: "short", day: "numeric" };
//     return new Date(dateStr).toLocaleDateString(undefined, options);
//   };

//   const filteredReviews = useMemo(() => {
//     if (filter === "all") return reviews;
//     return reviews.filter((review) => Number(review.starRating) === Number(filter));
//   }, [reviews, filter]);

//   const totalPages = Math.ceil(filteredReviews.length / REVIEWS_PER_PAGE);

//   const paginatedReviews = useMemo(() => {
//     return filteredReviews.slice(
//       (currentPage - 1) * REVIEWS_PER_PAGE,
//       currentPage * REVIEWS_PER_PAGE
//     );
//   }, [filteredReviews, currentPage]);

//   return (
//     <>
//       <Seo
//         title={
//           productData
//             ? `${productData.name} Reviews | Parish Audio`
//             : "Plugin Reviews | Parish Audio"
//         }
//         description={
//           productData
//             ? `Read customer reviews for ${productData.name} by Parish Audio. See ratings, feedback, and user impressions for this VST3 plugin.`
//             : "Read customer reviews for Parish Audio plugins."
//         }
//         canonical={
//           productData
//             ? `https://parishaudio.com/reviews/${productData.productURI}`
//             : "https://parishaudio.com/reviews"
//         }
//         image={
//           productData?.images?.[0]?.URI
//             ? `https://parishaudio.com${productData.images[0].URI}`
//             : "https://parishaudio.com/assets/og-default.jpg"
//         }
//         imageAlt={
//           productData?.images?.[0]?.altText || productData?.name || "Parish Audio reviews"
//         }
//         ogType="website"
//       />

//       {productData && (
//         <ProductReviewSchemaMarkup
//           productData={productData}
//           reviews={reviews}
//           averageRating={averageRating}
//         />
//       )}

//       {!productSKU ? (
//         <Box
//           sx={{
//             minHeight: height - 170,
//             display: "flex",
//             justifyContent: "center",
//             alignItems: "center",
//           }}
//         >
//           <LoadingSpinner />
//         </Box>
//       ) : (
//         <Box
//           sx={{
//             maxWidth: 1000,
//             margin: "auto",
//             padding: { xs: 1.5, md: 2 },
//             minHeight: height - 170,
//           }}
//         >
//           <Paper
//             sx={{
//               p: { xs: 2, md: 3 },
//               backgroundColor: style.color1,
//               color: style.color4,
//               borderRadius: 2,
//               boxShadow: 10,
//             }}
//           >
//             <Box
//               sx={{
//                 display: "grid",
//                 gridTemplateColumns: { xs: "1fr", md: "280px minmax(0, 1fr)" },
//                 gap: 3,
//                 alignItems: "start",
//               }}
//             >
//               <Box
//                 sx={{
//                   textAlign: "center",
//                   p: 2,
//                   borderRadius: 2,
//                   backgroundColor: "rgba(0,0,0,0.18)",
//                   boxShadow: 8,
//                 }}
//               >
//                 <Link
//                   to={`/products/${productData.productURI}`}
//                   style={{ textDecoration: "none", color: "inherit" }}
//                 >
//                   <Box
//                     component="img"
//                     src={productData.images?.[0]?.URI}
//                     alt={productData.images?.[0]?.altText}
//                     sx={{
//                       maxWidth: "90%",
//                       maxHeight: 230,
//                       objectFit: "contain",
//                       display: "block",
//                       mx: "auto",
//                     }}
//                   />
//                   <Typography
//                     variant="h5"
//                     sx={{ mt: 1.5, fontSize: "1.2rem", color: style.color3, fontWeight: 700 }}
//                   >
//                     {productData.name}
//                   </Typography>
//                 </Link>

//                 <Button
//                   component={Link}
//                   to={`/products/${productData.productURI}`}
//                   variant="outlined"
//                   sx={{
//                     mt: 2,
//                     color: style.color4,
//                     borderColor: style.color2,
//                     "&:hover": {
//                       borderColor: style.color3,
//                       backgroundColor: "rgba(255,255,255,0.05)",
//                     },
//                   }}
//                 >
//                   View Product
//                 </Button>
//               </Box>

//               <Box>
//                 <Typography
//                   variant="h1"
//                   sx={{
//                     fontSize: { xs: "2rem", md: "2.4rem" },
//                     fontWeight: 800,
//                     mb: 1,
//                     lineHeight: 1.1,
//                   }}
//                 >
//                   {productData.name} Reviews
//                 </Typography>

//                 <Typography
//                   variant="body1"
//                   sx={{
//                     color: style.color3,
//                     mb: 2,
//                     maxWidth: 700,
//                   }}
//                 >
//                   Read customer feedback, ratings, and impressions for {productData.name}.
//                 </Typography>

//                 <Stack
//                   direction={{ xs: "column", md: "row" }}
//                   spacing={3}
//                   sx={{ mb: 2.5 }}
//                 >
//                   <Box sx={{ minWidth: { md: 220 } }}>
//                     <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
//                       <Rating value={parseFloat(averageRating)} precision={0.1} readOnly />
//                       <Typography variant="h6" sx={{ fontWeight: 700 }}>
//                         {averageRating} / 5
//                       </Typography>
//                     </Stack>

//                     <Typography variant="body2" sx={{ color: style.color3 }}>
//                       Based on {reviews.length} review{reviews.length === 1 ? "" : "s"}
//                     </Typography>
//                   </Box>

//                   <Box sx={{ flexGrow: 1 }}>
//                     {[5, 4, 3, 2, 1].map((star) => {
//                       const count = ratingsBreakdown[5 - star] || 0;
//                       const percent = reviews.length ? (count / reviews.length) * 100 : 0;

//                       return (
//                         <Box
//                           key={star}
//                           sx={{
//                             display: "flex",
//                             alignItems: "center",
//                             gap: 1.5,
//                             mb: 1,
//                           }}
//                         >
//                           <Typography sx={{ width: 42, fontSize: "0.95rem" }}>
//                             {star} ★
//                           </Typography>
//                           <LinearProgress
//                             variant="determinate"
//                             value={percent}
//                             sx={{
//                               flexGrow: 1,
//                               height: 10,
//                               borderRadius: 999,
//                               backgroundColor: "rgba(255,255,255,0.14)",
//                               "& .MuiLinearProgress-bar": {
//                                 backgroundColor: style.color2,
//                               },
//                             }}
//                           />
//                           <Typography sx={{ width: 28, textAlign: "right", fontSize: "0.95rem" }}>
//                             {count}
//                           </Typography>
//                         </Box>
//                       );
//                     })}
//                   </Box>
//                 </Stack>

//                 <Divider sx={{ backgroundColor: style.color2, mb: 2 }} />

//                 <Stack
//                   direction={{ xs: "column", md: "row" }}
//                   spacing={1.5}
//                   sx={{ mb: 1 }}
//                 >
//                   <Button
//                     size="small"
//                     onClick={() => setLeaveReviewModalOpen(true)}
//                     sx={{
//                       minWidth: 150,
//                       color: style.color4,
//                       boxShadow: 5,
//                       backgroundColor: style.color1,
//                       filter: "brightness(1.2)",
//                       fontSize: "12pt",
//                       borderColor: style.color2,
//                       borderWidth: "1px",
//                       borderStyle: "solid",
//                       "&:hover": { backgroundColor: style.color2 },
//                     }}
//                   >
//                     Leave Review
//                   </Button>

//                   <Select
//                     value={filter}
//                     onChange={(e) => setFilter(e.target.value)}
//                     size="small"
//                     sx={{
//                       minWidth: 160,
//                       color: style.color4,
//                       boxShadow: 10,
//                       "& .MuiOutlinedInput-notchedOutline": {
//                         borderColor: style.color2,
//                       },
//                       "&:hover .MuiOutlinedInput-notchedOutline": {
//                         borderColor: style.color3,
//                       },
//                       "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
//                         borderColor: style.color3,
//                       },
//                       "& .MuiSelect-icon": {
//                         color: style.color4,
//                       },
//                     }}
//                   >
//                     <MenuItem value="all">All Ratings</MenuItem>
//                     {[5, 4, 3, 2, 1].map((num) => (
//                       <MenuItem key={num} value={num}>
//                         {num} Stars
//                       </MenuItem>
//                     ))}
//                   </Select>

//                   <Select
//                     value={sort}
//                     onChange={(e) => setSort(e.target.value)}
//                     size="small"
//                     sx={{
//                       minWidth: 160,
//                       color: style.color4,
//                       boxShadow: 10,
//                       "& .MuiOutlinedInput-notchedOutline": {
//                         borderColor: style.color2,
//                       },
//                       "&:hover .MuiOutlinedInput-notchedOutline": {
//                         borderColor: style.color3,
//                       },
//                       "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
//                         borderColor: style.color3,
//                       },
//                       "& .MuiSelect-icon": {
//                         color: style.color4,
//                       },
//                     }}
//                   >
//                     <MenuItem value="recent">Most Recent</MenuItem>
//                     <MenuItem value="helpful">Most Helpful</MenuItem>
//                   </Select>
//                 </Stack>
//               </Box>
//             </Box>
//           </Paper>

//           <Box mt={4}>
//             {paginatedReviews.length === 0 ? (
//               <Paper
//                 sx={{
//                   p: 3,
//                   backgroundColor: style.color1,
//                   color: style.color4,
//                   borderRadius: 2,
//                   boxShadow: 6,
//                 }}
//               >
//                 <Typography variant="body1">No reviews yet.</Typography>
//               </Paper>
//             ) : (
//               paginatedReviews.map((review) => (
//                 <Card
//                   key={review._id}
//                   sx={{
//                     mb: 2,
//                     backgroundColor: style.color4,
//                     color: "black",
//                     borderRadius: 2,
//                     boxShadow: 6,
//                   }}
//                 >
//                   <CardContent>
//                     <Stack
//                       direction={{ xs: "column", sm: "row" }}
//                       justifyContent="space-between"
//                       alignItems={{ xs: "flex-start", sm: "center" }}
//                       spacing={1}
//                     >
//                       <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
//                         <Typography sx={{ fontSize: "1.15rem", fontWeight: 700 }}>
//                           {review.userName}
//                         </Typography>
//                         <Rating value={review.starRating} readOnly precision={0.5} />
//                       </Box>

//                       <Typography
//                         variant="caption"
//                         sx={{ color: style.color1, fontStyle: "italic" }}
//                       >
//                         {formatDate(review.createdAt)}
//                       </Typography>
//                     </Stack>

//                     <Typography
//                       variant="body1"
//                       sx={{
//                         mt: 1.25,
//                         mb: 2,
//                         fontSize: "1rem",
//                         color: "black",
//                         whiteSpace: "pre-wrap",
//                       }}
//                     >
//                       {review.comment}
//                     </Typography>

//                     <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
//                       <Tooltip title="Helpful">
//                         <IconButton
//                           size="small"
//                           onClick={() => handleVote(review._id, "helpful")}
//                         >
//                           <ThumbUpIcon
//                             sx={{
//                               color:
//                                 user && review.foundHelpful?.includes(user.id)
//                                   ? "rgb(66, 135, 245)"
//                                   : style.color2,
//                               width: 28,
//                               height: 28,
//                             }}
//                           />
//                           <Typography variant="caption" sx={{ fontSize: "0.8em", ml: 0.5 }}>
//                             <strong>{review.helpfulCount}</strong>
//                           </Typography>
//                         </IconButton>
//                       </Tooltip>

//                       <Tooltip title="Not Helpful">
//                         <IconButton
//                           size="small"
//                           onClick={() => handleVote(review._id, "not_helpful")}
//                         >
//                           <ThumbDownIcon
//                             sx={{
//                               color:
//                                 user && review.foundNotHelpful?.includes(user.id)
//                                   ? "rgb(66, 135, 245)"
//                                   : style.color2,
//                               width: 28,
//                               height: 28,
//                             }}
//                           />
//                           <Typography variant="caption" sx={{ fontSize: "0.8em", ml: 0.5 }}>
//                             <strong>{review.notHelpfulCount}</strong>
//                           </Typography>
//                         </IconButton>
//                       </Tooltip>
//                     </Box>
//                   </CardContent>
//                 </Card>
//               ))
//             )}

//             {totalPages > 1 && (
//               <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
//                 <Pagination
//                   count={totalPages}
//                   page={currentPage}
//                   onChange={(e, value) => setCurrentPage(value)}
//                   color="primary"
//                   sx={{
//                     "& .MuiPaginationItem-root": {
//                       color: style.color4,
//                     },
//                   }}
//                 />
//               </Box>
//             )}
//           </Box>

//           <SubscribeRedirectModal
//             isOpen={isSubscribeRedirectModalOpen}
//             close={closeModal(setSubscribeRedirectModalOpen)}
//           />
//           <LoginRegisterModal
//             isOpen={isLoginRegisterModalOpen}
//             close={closeModal(setLoginRegisterModalOpen)}
//             loginComplete={loginCallback}
//           />
//           <LeaveReviewModal
//             sku={productSKU}
//             isOpen={isLeaveReviewModalOpen}
//             close={closeModal(setLeaveReviewModalOpen)}
//           />
//         </Box>
//       )}
//     </>
//   );
// }


// // import React, { useState, useContext, useEffect } from "react";
// // import axios from "axios";
// // import {
// //   Box,
// //   Card,
// //   CardContent,
// //   Typography,
// //   Rating,
// //   Button,
// //   Select,
// //   MenuItem,
// //   IconButton,
// //   Tooltip,
// //   LinearProgress,
// //   useMediaQuery,
// //   Pagination,
// // } from "@mui/material";
// // import { useTheme } from "@mui/material/styles";
// // import ThumbUpIcon from "@mui/icons-material/ThumbUp";
// // import ThumbDownIcon from "@mui/icons-material/ThumbDown";
// // import { UserContext } from "../../context/userContext/userContext";
// // import { useStyle } from "../../context/styleContext/styleContext";
// // import SubscribeRedirectModal from "../../components/SubscribeRedirectModal/SubscribeRedirectModal";
// // import LoginRegisterModal from "./LoginRegisterModal";
// // import LeaveReviewModal from "./leaveReviewModal";
// // import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";

// // const REVIEWS_PER_PAGE = 3;

// // export default function ProductReviewPage() {
// //   const { user } = useContext(UserContext);
// //   const { style } = useStyle();
// //   const theme = useTheme();
// //   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

// //   const [reviews, setReviews] = useState([]);
// //   const [productSKU, setProductSKU] = useState(null);
// //   const [productData, setProductData] = useState(null);
// //   const [filter, setFilter] = useState("all");
// //   const [sort, setSort] = useState("recent");
// //   const [currentPage, setCurrentPage] = useState(1);
// //   const [ratingsBreakdown, setRatingsBreakdown] = useState({});
// //   const [averageRating, setAverageRating] = useState(0);

// //   const [isSubscribeRedirectModalOpen, setSubscribeRedirectModalOpen] = useState(false);
// //   const [isLoginRegisterModalOpen, setLoginRegisterModalOpen] = useState(false);
// //   const [isLeaveReviewModalOpen, setLeaveReviewModalOpen] = useState(false);

// //   const [isLoginComplete, setIsLoginComplete] = useState(false); 
// //   const openModal = setModalFn => () => setModalFn(true);
// //   const closeModal = setModalFn => () => setModalFn(false);

// //   const [storedReviewId, setStoredReviewId] = useState()
// //   const [storedVote, setStoredVote] = useState()

// //   const loginCallback = async () => {

// //     setIsLoginComplete(true);

// //     const token = localStorage.getItem('token')
// //     if(!token)
// //     {
// //       console.log("NO TOKEN")
// //       return
// //     }

// //     const res = await axios.post("/api/mark_review", {
// //       token,
// //       reviewId:storedReviewId,
// //       type:storedVote,
// //     });

// //     getReviews();
// //     setLoginRegisterModalOpen(false);
// //     console.log("FINISHED!!")
// //   };

// //   const openLeaveReviewModal = () => setLeaveReviewModalOpen(true);
// //   const [height, setHeight] = useState(window.innerHeight)
  
// //   useEffect(() => {
// //     document.title = "Parish Audio | Reviews";
// //     getReviews();
// //   }, [filter, sort]);

// //   const getReviews = async () => {
// //     const productURI = window.location.pathname.split("/").pop();
// //     const { data: productInfo } = await axios.post("/get_product_by_uri", { productURI });

// //     if (!productInfo) return;

// //     setProductSKU(productInfo.SKU);
// //     setProductData(productInfo);

// //     const res = await axios.post("/get_all_product_reviews", {
// //       productSKU: productInfo.SKU,
// //       filter,
// //       sort,
// //     });

// //     const reviews = res.data || [];
// //     setReviews(reviews);

// //     const starCounts = [0, 0, 0, 0, 0]; // index 0 = 5 stars, index 4 = 1 star
// //     let total = 0;
// //     let sum = 0;

// //     reviews.forEach((r) => {
// //       const index = 5 - r.starRating;
// //       starCounts[index] = (starCounts[index] || 0) + 1;
// //       total++;
// //       sum += r.starRating;
// //     });

// //     const average = total ? (sum / total).toFixed(1) : 0;
// //     setAverageRating(average);
// //     setRatingsBreakdown(starCounts);
// //   };

// //   const handleVote = async (reviewId, type) => {

// //     const token = localStorage.getItem('token');
// //     if (!token ) {
// //       setStoredVote(type);
// //       setStoredReviewId(reviewId);
// //       setLoginRegisterModalOpen(true);
// //       return;
// //     }
// //     if (!user ) {
// //       setStoredVote(type);
// //       setStoredReviewId(reviewId);
// //       setLoginRegisterModalOpen(true);
// //       return;
// //     }

// //     const res = await axios.post("/api/mark_review", {
// //       token,
// //       reviewId,
// //       type,
// //     });

// //     getReviews();
// //     // getReviews();
// //   };


// //   const formatDate = (dateStr) => {
// //     const options = { year: "numeric", month: "short", day: "numeric" };
// //     return new Date(dateStr).toLocaleDateString(undefined, options);
// //   };

// //   const filteredReviews = filter === "all"
// //     ? reviews
// //     : reviews.filter(r => r.starRating === filter);

// //   const totalPages = Math.ceil(filteredReviews.length / REVIEWS_PER_PAGE);
// //   const paginatedReviews = filteredReviews.slice(
// //     (currentPage - 1) * REVIEWS_PER_PAGE,
// //     currentPage * REVIEWS_PER_PAGE
// //   );

// //   return (
// //     <>
// //       {!productSKU ? (
// //         <Box sx={{ minHeight: "80vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
// //           <LoadingSpinner />
// //         </Box>
// //       ) : (
// //         <Box sx={{ maxWidth: 1000, margin: "auto", padding: 2, minHeight:height - 170, }}>
// //           {/* Header */}
// //           <Box
// //             sx={{
// //               display: "flex",
// //               flexDirection: isMobile ? "column" : "row",
// //               gap: 2,
// //               backgroundColor:style.color1,
// //               borderRadius:'8px',
// //               alignItems: isMobile ? "center" : "flex-start",
              
// //             }}
// //           >
// //             {/* Product Thumbnail */}
// //             <Box
// //               sx={{
// //                 width: isMobile ? "auto" : "40%",
// //                 height: isMobile ? "auto" : "100%",
// //                 borderRadius: 2,
// //                 //border: `1px solid ${style.color2}`,
// //                 overflow: "hidden",
// //                 textAlign:'center',
// //                 padding:'10px',
                
                
// //               }}
// //             >
// //               <a href={'/products/'+ productData.productURI}>
// //               <img
// //                 src={productData.images[0].URI}
// //                 alt={productData.images[0].altText}
// //                 style={{
                  
// //                   height: isMobile ? "auto" : "auto",
// //                   objectFit: "cover",
// //                   borderRadius: 8,
// //                   maxHeight:'230px',
// //                   maxWidth:'90%',
// //                   width:'auto',
// //                   margin:'auto',
// //                 }}
// //               />
// //               </a>
// //               <h2 style={{marginTop:'10px', fontSize:'1.2em', color:style.color3}}>{productData.name}</h2>
// //             </Box>

// //             {/* Average Rating & Filters */}
// //             <Box
// //               sx={{
// //                 flexGrow: 1,
// //                 backgroundColor:style.color1,
// //                 borderRadius: 2,
// //                 padding: 1,
// //                 width: isMobile ? "95%" : "95%",
// //                 color:style.color4,
// //               }}
// //             >
// //               <Box sx={{ display:'flex', flexDirection:'column'}}>
// //               </Box>
// //               <h1 style={{marginTop:'10px'}}>Reviews</h1>
// //               <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 2 }}>
// //                 <Rating value={parseFloat(averageRating)} precision={0.1} readOnly />
// //                 <Typography variant="h6">{averageRating} / 5</Typography>
// //               </Box>
// //               {/* Breakdown Chart */}
// //               {ratingsBreakdown &&
// //                 [5, 4, 3, 2, 1].map((star, i) => {
// //                   const count = ratingsBreakdown[5 - star] || 0;
// //                   const percent = reviews.length ? (count / reviews.length) * 100 : 0;
// //                   return (
// //                     <Box key={star} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
// //                       <Typography sx={{ width: 40 }}>{star} ★</Typography>
// //                       <LinearProgress
// //                         variant="determinate"
// //                         value={percent}
// //                         sx={{ flexGrow: 1, height: 10, borderRadius: 5 }}
// //                       />
// //                       <Typography sx={{ width: 30 }}>{count}</Typography>
// //                     </Box>
// //                   );
// //                 })}

// //               {/* Filter / Sort */}
// //               <Box sx={{ display: "flex", mt: 2, pb:1, flexDirection: isMobile ? "column" : "row", }}>
// //               <Button size='small' onClick={openLeaveReviewModal} sx={{minWidth:'150px',ml:1, mb:2, color: style.color4, display: 'block', boxShadow:5, backgroundColor: style.color1, filter: 'brightness(1.2)', fontSize: '12pt',  marginTop: '0px', borderColor: style.color2, borderWidth: '1px', borderStyle: 'solid', '&:hover': { backgroundColor: style.color2 } }}>Leave Review</Button>
// //               <Select
// //                 value={filter}
// //                 onChange={(e) => setFilter(e.target.value)}
// //                 size="small"
                
// //                 sx={{
// //                   color: style.color4,
// //                   boxShadow:10,
// //                   ml:1,
// //                   mb:2,
// //                   '& .MuiOutlinedInput-notchedOutline': {
// //                     borderColor: style.color2,
// //                   },
// //                   '&:hover .MuiOutlinedInput-notchedOutline': {
// //                     borderColor: style.color3,
// //                   },
// //                   '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
// //                     borderColor: style.color3,
// //                   },
// //                   '& .MuiSelect-icon': {
// //                     color: style.color4, // This changes the dropdown arrow
// //                   },
// //                 }}
// //               >
// //                 <MenuItem value="all">All Ratings</MenuItem>
// //                 {[5, 4, 3, 2, 1].map(num => (
// //                   <MenuItem key={num} value={num}>{num} Stars</MenuItem>
// //                 ))}
// //               </Select>
// //                 <Select sx={{
// //                   color: style.color4,
// //                   ml:1,
// //                   boxShadow:10,
// //                   size:'small',
// //                   mb:2,
// //                   '& .MuiOutlinedInput-notchedOutline': {
// //                     borderColor: style.color2,
// //                   },
// //                   '&:hover .MuiOutlinedInput-notchedOutline': {
// //                     borderColor: style.color3,
// //                   },
// //                   '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
// //                     borderColor: style.color3,
// //                   },
// //                   '& .MuiSelect-icon': {
// //                     color: style.color4, // This changes the dropdown arrow
// //                   },
// //                 }}
// //                  value={sort} onChange={(e) => setSort(e.target.value)} size="small">
// //                   <MenuItem value="recent">Most Recent</MenuItem>
// //                   <MenuItem value="helpful">Most Helpful</MenuItem>
// //                 </Select>
// //                 </Box>
// //             </Box>
// //           </Box>

// //           {/* Reviews */}
// //           <Box mt={4}>
// //             {paginatedReviews.length === 0 ? (
// //               <Typography variant="body1">No reviews yet.</Typography>
// //             ) : (
// //               paginatedReviews.map(review => (
// //                 <Card key={review._id} sx={{ mb: 2, backgroundColor:style.color4, color:'black' }}>
// //                   <CardContent>
// //                     <Box sx={{ display: "flex", flex:1, justifyContent: "left", alignItems: "center"}}>
// //                       <Typography sx={{fontSize:'1.3em'}}>{review.userName}</Typography>
// //                       <Rating value={review.starRating} readOnly precision={0.5} sx={{ml:2}}/>
// //                     </Box>

// //                     <Typography variant="caption" sx={{ color: style.color1, fontStyle: "italic", display: "block" }}>
// //                       {formatDate(review.createdAt)}
// //                     </Typography>

// //                     <Typography variant="body2" sx={{ mt: 0.5, mb:2, fontSize:'1em',backgroundColor:style.color4, color:'black' }}>{review.comment}</Typography>

// //                     <Box sx={{ display: "flex", gap: 1, mt: 1, backgroundColor:style.color4, }}>
// //                     <Tooltip title="Helpful">
// //                     <IconButton
// //                       size="small"
// //                       onClick={() => handleVote(review._id, "helpful")}
// //                     >
// //                       {user && (
// //                           <ThumbUpIcon
// //                           sx={{
// //                             color: review.foundHelpful.includes(user.id)
// //                               ? 'rgb(66, 135, 245)' // highlighted color
// //                               : style.color2, // default color
// //                             width:'30px',
// //                             height:'30px'
// //                           }}
// //                           />
// //                       )}
// //                        {!user && (
// //                       <ThumbUpIcon
// //                         sx={{
// //                           color: style.color2,
// //                           width:'30px',
// //                           height:'30px'
// //                         }}
// //                       />)}
// //                       <Typography variant="caption" sx={{fontSize:'0.8em', ml: 0.5 }}>
// //                         <strong>{review.helpfulCount}</strong>
// //                       </Typography>
// //                     </IconButton>
// //                   </Tooltip>
// //                       <Tooltip title="Not Helpful">
// //                         <IconButton
// //                       size="small"
// //                       onClick={() => handleVote(review._id, "not_helpful")}
// //                     >
// //                       {user && (
// //                         <ThumbDownIcon
// //                         sx={{
// //                           color: review.foundNotHelpful.includes(user.id)
// //                             ? 'rgb(66, 135, 245)' // highlighted color
// //                             : style.color2, // default color
// //                           width:'30px',
// //                           height:'30px'
// //                         }}
// //                       />

// //                       )}
// //                       {!user &&(
// //                         <ThumbDownIcon
// //                         sx={{
// //                           color: style.color2,
// //                           width:'30px',
// //                           height:'30px'
// //                         }}
// //                         />
// //                       )}
                     
// //                       <Typography variant="caption" sx={{fontSize:'0.8em', ml: 0.5 }}>
// //                         <strong>{review.notHelpfulCount}</strong>
// //                       </Typography>
// //                     </IconButton>
// //                       </Tooltip>
// //                     </Box>
// //                   </CardContent>
// //                 </Card>
// //               ))
// //             )}

// //             {/* Pagination */}
// //             {totalPages > 1 && (
// //               <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
// //                 <Pagination
// //                   count={totalPages}
// //                   page={currentPage}
// //                   onChange={(e, value) => setCurrentPage(value)}
// //                   color='primary'
// //                   sx={{color:style.color4}}
// //                 />
// //               </Box>
// //             )}
// //           </Box>

// //           {/* Modals */}
// //           <SubscribeRedirectModal isOpen={isSubscribeRedirectModalOpen} close={closeModal(setSubscribeRedirectModalOpen)} />
// //           <LoginRegisterModal isOpen={isLoginRegisterModalOpen} close={closeModal(setLoginRegisterModalOpen)} loginComplete={loginCallback}/>
// //           <LeaveReviewModal sku={productSKU} isOpen={isLeaveReviewModalOpen} close={closeModal(setLeaveReviewModalOpen)} />

// //         </Box>
// //       )}
// //     </>
// //   );
// // }


